/**
 * Field limits of an HQ customer row. These mirror the backend's shared field types
 * (docs/auth-and-admin/hq-customers-contract.md §2.3); a backend test reads this file and pins the
 * numbers, so the two copies cannot drift. They exist here so the form can refuse a value the
 * server would answer with a 422, before sending it.
 */
export const NPS_MIN = 0;
export const NPS_MAX = 10;
export const TAGS_MAX_COUNT = 20;
export const TAG_MAX_LENGTH = 40;
export const PHONE_MAX_LENGTH = 40;
export const NOTES_MAX_LENGTH = 5000;
export const NAME_MAX_LENGTH = 200;
export const COMM_SUBJECT_MAX_LENGTH = 200;
export const COMM_BODY_MAX_LENGTH = 10000;

/**
 * Lengths are counted in Unicode code points, as the server counts them (Python `len`), so an
 * emoji is one character on both sides. A string's `.length` (and an input's `maxLength`) counts
 * UTF-16 code units instead, which would refuse an astral character the server accepts.
 */
export const codePointLength = (text) => [...text].length;

/** `text` cut to `max` code points (never in the middle of a surrogate pair). */
export const clampToCodePoints = (text, max) => (codePointLength(text) > max ? [...text].slice(0, max).join("") : text);

/** A tag as the server stores it: ends stripped, every run of whitespace collapsed to one space. */
export const normalizeTag = (raw) => raw.replace(/\s+/g, " ").trim();

/**
 * The tags a list holds once normalised: empties dropped, duplicates (case-insensitive) removed,
 * the first spelling and the order kept. The server applies the same rule, so what the editor
 * shows is what is stored.
 */
export function normalizeTags(list) {
  const seen = new Set();
  const out = [];
  list.forEach((raw) => {
    const tag = normalizeTag(raw);
    const key = tag.toLowerCase();
    if (!tag || seen.has(key)) return;
    seen.add(key);
    out.push(tag);
  });
  return out;
}

/**
 * Why a tag cannot be stored, `null` when it can, in the server's order (platform_customers
 * `_clean_tag`): "control" (a character a single line may not hold, see hasForbiddenCharacter;
 * the server refuses it rather than dropping it), "empty" (nothing left once normalised),
 * "invisible" (characters, none of which renders) or "tooLong" (over TAG_MAX_LENGTH once
 * normalised).
 */
export function tagProblem(raw) {
  const tag = normalizeTag(raw);
  if (hasForbiddenCharacter(tag)) return "control";
  if (!tag) return "empty";
  if (!hasVisibleCharacter(tag)) return "invisible";
  if (codePointLength(tag) > TAG_MAX_LENGTH) return "tooLong";
  return null;
}

/**
 * The typed or pasted text of the tag input, split on commas into the tags it commits and the
 * draft that stays in the field. `commitRest` (Enter, a paste, leaving the field) commits the
 * text after the last comma too; otherwise it is still being typed. `a,b,c` is three tags.
 */
export function splitTagInput(text, { commitRest = false } = {}) {
  const pieces = text.split(",");
  const rest = commitRest ? "" : pieces.pop();
  return { parts: pieces, rest };
}

// The text rules every HQ text field shares, mirroring platform_customers.has_forbidden_chars and
// has_visible_char. One sample file (__tests__/hqTextRules.samples.json) is run through both sides,
// so they cannot drift.
//
// Characters no stored text may hold: control characters (Cc) and unpaired surrogates (Cs; they
// have no UTF-8 encoding, so the database refuses them). A single line also refuses the line and
// paragraph separators U+2028/U+2029 (U+0085 is Cc); a multi-line text keeps newline and tab and
// those two separators, and still refuses a carriage return.
const SINGLE_LINE_FORBIDDEN = /[\p{Cc}\p{Cs}\u2028\u2029]/u;
// eslint-disable-next-line no-control-regex
const MULTI_LINE_FORBIDDEN = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\p{Cs}]/u;

/** Whether `text` holds a character the field may not store (single line unless `multiline`). */
export const hasForbiddenCharacter = (text, { multiline = false } = {}) =>
  (multiline ? MULTI_LINE_FORBIDDEN : SINGLE_LINE_FORBIDDEN).test(text);

// A character that renders on its own: not whitespace or a separator, not control, format,
// private-use or surrogate, not a mark that only modifies a base character (Mn, Me), not
// default-ignorable (the Hangul fillers U+115F, U+1160, U+3164, U+FFA0 among them, and the code
// points Unicode reserves as default-ignorable while unassigned) and not U+2800 (braille pattern
// blank). An unassigned code point (Cn) is unknown, not blank, so it counts as visible: refusing
// it made the verdict depend on the Unicode version (the server's Python knows an older one).
const VISIBLE_CHARACTER = /[^\s\p{Z}\p{Cc}\p{Cf}\p{Co}\p{Cs}\p{Mn}\p{Me}\p{Default_Ignorable_Code_Point}\u2800]/u;

/** Whether `text` has a character that renders (the server's `has_visible_char`). */
export const hasVisibleCharacter = (text) => VISIBLE_CHARACTER.test(text);

// The whitespace the server strips from a name's ends (Python's str.strip): JavaScript's `\s`
// minus U+FEFF, which the server keeps (and which renders as nothing: "invisible", not "empty").
const SERVER_STRIPPED_WHITESPACE = /[^\S\ufeff]/gu;

/**
 * Why a client's name cannot be stored, `null` when it can: "control" (a character a single line
 * may not hold), "empty" (blank), "invisible" (characters, none of which renders) or "tooLong"
 * (over NAME_MAX_LENGTH code points once the ends are stripped, as the server measures it).
 */
export function nameProblem(raw) {
  if (hasForbiddenCharacter(raw)) return "control";
  if (!hasVisibleCharacter(raw)) return raw.replace(SERVER_STRIPPED_WHITESPACE, "") ? "invisible" : "empty";
  if (codePointLength(raw.trim()) > NAME_MAX_LENGTH) return "tooLong";
  return null;
}

/**
 * Why the text of the NPS input cannot be saved, `null` when it can. An empty text is valid: it
 * clears the score. Anything else must be a plain integer in NPS_MIN..NPS_MAX (no sign, decimal
 * point, exponent or padding), because the server takes a strict integer.
 */
export function npsProblem(text) {
  if (text === "") return null;
  if (!/^\d+$/.test(text)) return "invalid";
  const n = Number(text);
  return n >= NPS_MIN && n <= NPS_MAX ? null : "invalid";
}
